import { useContext, useState } from 'react';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import AudioPlayer from '@/components/app/contentPlayer/AudioPlayer';
import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import VideoPlayer from '@/components/app/contentPlayer/VideoPlayer';
import AppBackground from '@/components/app/layout/AppBackground';
import DiscountModal from '@/components/app/layout/DiscountModal';
import Header from '@/components/app/layout/HeaderRefresh';
import { ShareContext } from '@/components/app/shareFreeContent/ShareProvider';
import Sidebar from '@/components/app/sidebar/Sidebar';
import ControlFlow from '@/components/SimplifiedSignup/configs/ControlModal/Flow';
import {
	SimplifiedSignupContextProvider,
	useSimplifiedSignupContext,
} from '@/components/SimplifiedSignup/Context';
import { ActiveScreenConfig } from '@/components/SimplifiedSignup/Context/types';
import SimplifiedSignupModal from '@/components/SimplifiedSignup/SimplifiedSignupModal';
import UpsellFooter from '@/components/UpsellFooter';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAppLoadedState, useThemeState } from '@/hooks/store';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { calmLogger } from '@/utils/calmLogger';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';

import { CookiePreferencesBannerContext } from '../CookiePreferencesBannerProvider';
import { OfferBannerContext } from '../OfferBannerProvider';
import ShareContentBanner from '../ShareContentBanner';
import ShareContentModal from '../ShareContentModal';
import { menuItems } from './data';
import { Container, Main, Wrapper } from './styles';
import { AppLayoutProps } from './types';

const AppLayout = ({ children, appHeaderProps }: AppLayoutProps) => {
	const appLoaded = useAppLoadedState();
	const theme = useThemeState();
	const { onShowSharePayment, sidebarCta, isShareContentRecipient } = useContext(ShareContext);
	const { onPause, contentType, isZenMode, playerMode } = useContext(MediaPlayerContext);
	const { fixedBottomHeight, isOpen: isCookieBannerOpen } = useContext(CookiePreferencesBannerContext);
	const { isBannerVisible } = useContext(OfferBannerContext);
	const { flowConfig = ControlFlow } = useSimplifiedSignupContext();
	const { isModalOpen } = useSimplifiedSignupModalContext();
	const { isEnrolled, isLoading } = useAmplitudeExperiment(AmplitudeExperimentNames.WEB_APP_REDESIGN_2);

	const [isNavOpen, setIsNavOpen] = useState(false);

	const onShareBannerCtaPress = () => {
		if (playerMode === 'playing') {
			onPause();
		}
		onShowSharePayment('banner').catch(error =>
			calmLogger.error('Error in AppLayout onShowSharePayment', {}, error),
		);
	};

	if (!theme.webAppLayout) return <>{children}</>;

	return (
		<>
			<AppBackground isZenMode={isZenMode} />
			<Wrapper
				$isZenMode={isZenMode}
				$isLargePlayer={theme.webAppLayout === 'player'}
				$isBannerVisible={isBannerVisible}
				$isWebAppRedesign={!isLoading && isEnrolled}
			>
				<Sidebar
					menuItems={menuItems}
					upsellButtonCopy={isShareContentRecipient ? sidebarCta : undefined}
					onUpsellPress={isShareContentRecipient ? () => onShowSharePayment('sidebar') : undefined}
					isUpsellButtonVisible={isShareContentRecipient}
					bottomOffset={isCookieBannerOpen ? fixedBottomHeight : 0}
				/>

				<Main id={MAIN_CONTENT_ID}>
					<SimplifiedSignupContextProvider flowConfig={flowConfig}>
						<Header isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} appHeaderProps={appHeaderProps} />

						<Container
							$isVisible={appLoaded}
							$hasBanner={isShareContentRecipient}
							$bottomOffset={isCookieBannerOpen ? fixedBottomHeight : 0}
							$isLargePlayer={theme.webAppLayout === 'player'}
						>
							<ShareContentBanner
								showShareContent={isShareContentRecipient}
								onPress={onShareBannerCtaPress}
							/>
							<ShareContentModal />

							{children}
						</Container>
						{isModalOpen && (
							<SimplifiedSignupModal
								handleModalClose={async (activeScreenConfig: ActiveScreenConfig) => {
									if (activeScreenConfig.name === 'handoff') {
										await Promise.all([location.reload()]);
									}
								}}
							/>
						)}
					</SimplifiedSignupContextProvider>
				</Main>
				{contentType === 'video' && <VideoPlayer />}
				{contentType === 'audio' && <AudioPlayer />}
				<UpsellFooter />
			</Wrapper>
			<DiscountModal />
		</>
	);
};

export default AppLayout;
